import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaHistory, FaCommentDots } from 'react-icons/fa';
import './AIPage.css';

const AIPage = () => {
  const [historySaved, setHistorySaved] = useState(false);
  const [savingHistory, setSavingHistory] = useState(false);
  const [input, setInput] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  // Load history from backend
  const fetchHistory = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/ai_prompt_history`);
      if (!res.ok) throw new Error('Failed to fetch history');
      const data = await res.json();
      setHistory(data.history || []);
    } catch (err) {
      setHistory([]);
    }
  };
  useEffect(() => {
    fetchHistory();
  }, []);

  // Delete history row by id
  const handleDeleteHistory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this history item?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/ai_prompt_history/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete history');
      setHistory(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      alert('Error deleting history: ' + err.message);
    }
  };
  const [promptOptions, setPromptOptions] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const responseRef = useRef(null);

  useEffect(() => {
    if (responseRef.current) {
      responseRef.current.scrollTop = responseRef.current.scrollHeight;
    }
  }, [response]);

  useEffect(() => {
    // Fetch prompt options from /api/ai_prompts
    const fetchPrompts = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/ai_prompts`);
        if (!res.ok) throw new Error('Failed to fetch prompts');
        const data = await res.json();
        setPromptOptions(data.prompts || []);
      } catch (err) {
        setPromptOptions([]);
      }
    };
    fetchPrompts();
  }, []);

  const handlePromptSelect = (e) => {
    const id = e.target.value;
    setSelectedPromptId(id);
    const selected = promptOptions.find(p => String(p.id) === String(id));
    if (selected) {
      setInput(selected.prompt);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponse('');
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: input }),
      });
      if (!res.ok) throw new Error('API error, Please try again later.');
      const data = await res.json();
      setResponse(data.response || '');
    } catch (err) {
      setResponse('Error: ' + err.message);
    }
    setLoading(false);
  };

  return (
    <div className="ai-page-container">
  <h2 className="ai-title"><FaRobot style={{marginRight:8}}/>JACA - AI Coach</h2>
      <div className="ai-cards-container">
        {/* Card 1: Input */}
        <div className="ai-card">
          <div className="ai-card-header"><FaCommentDots style={{marginRight:6}}/> Ask a Question</div>
          <div className="ai-prompt-dropdown-container">
            <label htmlFor="ai-prompt-dropdown" className="ai-prompt-label">Choose a saved context:(optional)</label>
            <select
              id="ai-prompt-dropdown"
              value={selectedPromptId}
              onChange={handlePromptSelect}
              className="ai-prompt-dropdown"
            >
              <option value="">-- Select a prompt --</option>1
              {promptOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.prompt.length > 60 ? opt.prompt.slice(0, 60) + '...' : opt.prompt}</option>
              ))}
            </select>
          </div>
          <form className="ai-form-vertical" onSubmit={handleSubmit}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Type your question..."
              className="ai-input"
              disabled={loading}
              style={{marginBottom:16, resize:'vertical', minHeight: '90px', maxHeight: '500px', boxSizing: 'border-box', height: '480px', verticalAlign: 'top'}}
            />
            <button type="submit" className="ai-submit-btn" disabled={loading || !input.trim()}>
              {loading ? 'Thinking...' : 'Submit'}
            </button>
          </form>
        </div>
        {/* Card 2: Response */}
        <div className="ai-card">
          <div className="ai-card-header"><FaRobot style={{marginRight:6}}/> Response</div>
          <div className="ai-response-box" ref={responseRef}>
            <textarea
              value={response}
              readOnly
              className="ai-readonly-textarea"
              rows={8}
              style={{background:'#f0f8ff', border:'1px solid #e0e0e0', borderRadius:8, padding:10}}
            />
          </div>
          {historySaved && (
            <div className="ai-history-saved-label">Saved!</div>
          )}
          <button
            className="ai-save-history-btn"
            style={{marginTop:16, width:'100%', position:'relative'}}
            disabled={!input.trim() || !response.trim() || savingHistory}
            onClick={async () => {
              setSavingHistory(true);
              setHistorySaved(false);
              const user_id = 1;
              try {
                const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/ai_prompt_history`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ user_id, prompt: input, response }),
                });
                if (!res.ok) throw new Error('Failed to save history');
                setHistory([]); // Clear history box immediately
                await fetchHistory(); // Reload from API
                setHistorySaved(true);
              } catch (err) {
                alert('Error saving to history: ' + err.message);
              }
              setSavingHistory(false);
            }}
          >
            {savingHistory ? (
              <span className="ai-spinner" style={{position:'absolute', left:'50%', transform:'translateX(-50%)', top:'50%', marginTop:'-10px'}}></span>
            ) : 'Save to History'}
          </button>
        </div>
        {/* Card 3: History */}
        <div className="ai-card">
          <div className="ai-card-header"><FaHistory style={{marginRight:6}}/> History</div>
          <div className="ai-history-box">
            {history.length === 0 ? (
              <div className="ai-history-empty">No history yet.</div>
            ) : (
              <ul className="ai-history-list">
                {history.map((item) => (
                  <li key={item.id || item.creation_date || item.prompt} className="ai-history-item">
                    <div className="ai-history-date"><strong>Date:</strong> {item.creation_date ? new Date(item.creation_date).toLocaleString() : ''}</div>
                    <div className="ai-history-prompt"><strong>Q:</strong> {item.prompt}</div>
                    <div className="ai-history-response"><strong>A:</strong> {item.response}</div>
                    {item.id && (
                      <button
                        className="ai-delete-history-btn"
                        style={{marginTop:8, background:'#e74c3c', color:'#fff', border:'none', borderRadius:4, padding:'4px 10px', cursor:'pointer'}}
                        onClick={() => handleDeleteHistory(item.id)}
                      >
                        Delete
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPage;
